import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { getPlanters, Planter } from '../api/plantersService';
import { getHubs, Hub } from '../api/hubsService';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Generate consistent colors for hubs
const generateHubColor = (hubId: number): string => {
  const colors = [
    '#ef4444', // red
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#f97316', // orange
    '#6366f1', // indigo
  ];
  return colors[hubId % colors.length];
};

const planterPipelinePopupLine = (planter: Planter): string | null => {
  if (!planter.pathKind) return null;
  const stepLabels: Record<string, string> = {
    training: 'Training',
    funding: 'Funding',
    post_funding: 'Post-funding',
  };
  const step = planter.effectivePlantingStep ?? planter.plantingStep;
  const pathLabel = planter.pathKind === 'growth' ? 'Growth path' : 'Planting path';
  if (planter.pathKind === 'planting' && step) {
    return `${pathLabel} · ${stepLabels[step] ?? step.replace(/_/g, ' ')}`;
  }
  return pathLabel;
};

// Component to display hub labels on map
const HubLabels: React.FC<{ hubs: Hub[] }> = ({ hubs }) => {
  const map = useMap();

  useEffect(() => {
    const labels: L.Marker[] = [];

    hubs.forEach(hub => {
      if (hub.regionGeoJson) {
        const color = generateHubColor(hub.id);
        const layer = L.geoJSON(hub.regionGeoJson);
        const bounds = layer.getBounds();
        const center = bounds.getCenter();
        
        const label = L.marker(center, {
          icon: L.divIcon({
            className: 'hub-label',
            html: `<div style="background: white; padding: 4px 8px; border-radius: 4px; border: 2px solid ${color}; font-size: 13px; font-weight: bold; white-space: nowrap; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">${hub.name}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          })
        }).addTo(map);
        
        labels.push(label);
      }
    });

    return () => {
      labels.forEach(label => map.removeLayer(label));
    };
  }, [map, hubs]);

  return null;
};

const MapView: React.FC = () => {
  const [showHubs, setShowHubs] = useState(true);
  const [showPlanters, setShowPlanters] = useState(true);

  // Fetch planters
  const { data: plantersData, isLoading: plantersLoading } = useQuery({
    queryKey: ['planters', { limit: 500 }],
    queryFn: () => getPlanters({ limit: 500 }),
  });

  // Fetch hubs
  const { data: hubsData, isLoading: hubsLoading } = useQuery({
    queryKey: ['hubs', { limit: 100, isActive: true }],
    queryFn: () => getHubs({ limit: 100, isActive: true }),
  });

  const planters = plantersData?.data || [];
  const hubs: Hub[] = hubsData?.data || [];

  // Filter planters with valid coordinates
  const plantersWithCoords = planters.filter(p => p.latitude && p.longitude);

  return (
    <Layout>
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Map View</h1>
              <p className="mt-1 text-sm text-gray-600">
                View all hubs and church planters on the map
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowHubs(!showHubs)}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  showHubs
                    ? 'border-transparent text-white bg-green-600 hover:bg-green-700'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                {showHubs ? 'Hide' : 'Show'} Hubs
              </button>
              <button
                onClick={() => setShowPlanters(!showPlanters)}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  showPlanters
                    ? 'border-transparent text-white bg-primary-600 hover:bg-primary-700'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {showPlanters ? 'Hide' : 'Show'} Planters
              </button>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          {plantersLoading || hubsLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-sm text-gray-600">Loading map data...</p>
              </div>
            </div>
          ) : (
            <MapContainer
              style={{ height: '100%', width: '100%' }}
              center={[32.7, -89.7]}
              zoom={7}
            >
              <TileLayer 
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
                attribution="&copy; OpenStreetMap contributors" 
              />

              {/* Display Hub Regions */}
              {showHubs && hubs.map(hub => {
                if (!hub.regionGeoJson) return null;
                const color = generateHubColor(hub.id);
                return (
                  <GeoJSON
                    key={`hub-${hub.id}`}
                    data={hub.regionGeoJson}
                    style={{
                      fillColor: color,
                      fillOpacity: 0.15,
                      color: color,
                      weight: 2,
                      dashArray: '5, 5',
                    }}
                    onEachFeature={(feature, layer) => {
                      layer.bindPopup(`
                        <div class="p-2">
                          <h3 class="font-bold text-base text-gray-900">${hub.name}</h3>
                          ${hub.description ? `<p class="text-sm text-gray-600 mt-1">${hub.description}</p>` : ''}
                          <p class="text-xs text-gray-500 mt-2">Hub Region</p>
                        </div>
                      `);
                    }}
                  />
                );
              })}
              
              {/* Display Hub Labels */}
              {showHubs && <HubLabels hubs={hubs} />}

              {/* Display Planter Markers */}
              {showPlanters && plantersWithCoords.map(planter => (
                <Marker
                  key={`planter-${planter.id}`}
                  position={[planter.latitude!, planter.longitude!]}
                >
                  <Popup>
                    <div className="p-2" style={{ minWidth: '200px' }}>
                      <h3 className="font-bold text-base text-gray-900">{planter.name}</h3>
                      {planter.spouseName && (
                        <p className="text-sm text-gray-600">& {planter.spouseName}</p>
                      )}
                      {planter.churchName && (
                        <p className="text-sm text-gray-700 mt-2">
                          <strong>Church:</strong> {planter.churchName}
                        </p>
                      )}
                      {planter.city && planter.state && (
                        <p className="text-sm text-gray-700">
                          <strong>Location:</strong> {planter.city}, {planter.state}
                        </p>
                      )}
                      {planter.assignedHub && (
                        <p className="text-sm text-gray-700">
                          <strong>Hub:</strong> {planter.assignedHub}
                        </p>
                      )}
                      {planterPipelinePopupLine(planter) && (
                        <p className="text-sm text-gray-700 mt-2">
                          <strong>Pipeline:</strong> {planterPipelinePopupLine(planter)}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Legend */}
        <div className="bg-white border-t border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 text-sm">
              {showHubs && (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-green-600 bg-green-100 rounded mr-2" style={{ borderStyle: 'dashed' }}></div>
                  <span className="text-gray-700">Hub Regions</span>
                </div>
              )}
              {showPlanters && (
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span className="text-gray-700">Church Planters</span>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {showPlanters && (
                <span>Showing {plantersWithCoords.length} of {planters.length} planters</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MapView;

